import {
  Avatar, Badge, Box, Button, CircularProgress, CircularProgressLabel,
  Flex, Grid, Heading, Progress, SimpleGrid, Text, Tooltip,
  useColorModeValue,
} from '@chakra-ui/react'
import Cookies from 'js-cookie'
import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { GET_USER_BY_ID } from 'store/users'

// ─── Badge definitions ──────────────────────────────────────────────────────
const BADGE_DEFS = [
  { icon: '⭐', name: 'มือใหม่', desc: 'ทำข้อสอบครั้งแรก', type: 'count', threshold: 1, color: 'yellow.400' },
  { icon: '🎯', name: 'ตั้งใจ', desc: 'ทำข้อสอบ 15 ครั้ง', type: 'count', threshold: 15, color: 'orange.400' },
  { icon: '💪', name: 'นักสู้', desc: 'ทำข้อสอบ 50 ครั้ง', type: 'count', threshold: 50, color: 'red.400' },
  { icon: '📚', name: 'ขยันเรียน', desc: 'ทำข้อสอบ 100 ครั้ง', type: 'count', threshold: 100, color: 'blue.400' },
  { icon: '🎓', name: 'หนอนหนังสือ', desc: 'ทำข้อสอบ 500 ครั้ง', type: 'count', threshold: 500, color: 'purple.500' },
  { icon: '🎖️', name: 'แม่นยำ', desc: 'คะแนนเฉลี่ย 70%+', type: 'avg', threshold: 70, color: 'teal.400' },
  { icon: '🏅', name: 'เก่งมาก', desc: 'คะแนนเฉลี่ย 80%+', type: 'avg', threshold: 80, color: 'cyan.500' },
  { icon: '🏆', name: 'ยอดเยี่ยม', desc: 'เคยได้คะแนน 90%+', type: 'best', threshold: 90, color: 'gold' },
]

const COUNT_MILESTONES = [1, 5, 10, 20, 50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 1000]

function getScoreColor(pct) {
  if (pct >= 80) return 'green.400'
  if (pct >= 60) return 'yellow.400'
  return 'red.400'
}

function getScoreScheme(pct) {
  if (pct >= 80) return 'green'
  if (pct >= 60) return 'yellow'
  return 'red'
}

function getMotivation(totalExams, avgPct) {
  if (totalExams === 0) return 'ยังไม่เคยทำข้อสอบ มาเริ่มกันเลย! 🚀'
  if (avgPct >= 90) return 'เก่งสุดๆ เลย! เป็นอัจฉริยะของห้องเลย 🌟'
  if (avgPct >= 80) return 'เยี่ยมมากเลย! เก่งจริงๆ 🏆'
  if (avgPct >= 70) return 'ทำได้ดีมาก! สู้ต่อไปนะ 💪'
  if (avgPct >= 60) return 'กำลังพัฒนาขึ้นเรื่อยๆ เดี๋ยวก็เก่ง! 📈'
  return 'ฝึกทำบ่อยๆ แล้วจะเก่งขึ้นแน่นอน! 🎯'
}

// ─── Sub-components ──────────────────────────────────────────────────────────
function StatMini({ icon, label, value, unit, bg }) {
  return (
    <Box bg={bg} borderRadius='2xl' p={4} textAlign='center' boxShadow='md'>
      <Text fontSize='2xl'>{icon}</Text>
      <Text fontSize='2xl' fontWeight='black' color='white' lineHeight={1.1}>{value ?? '-'}</Text>
      <Text fontSize='xs' color='whiteAlpha.800' fontWeight='bold'>{unit}</Text>
      <Text fontSize='xs' color='whiteAlpha.700' mt={1}>{label}</Text>
    </Box>
  )
}

function BadgeCard({ badge, earned }) {
  return (
    <Tooltip label={badge.desc} hasArrow placement='top'>
      <Box
        borderRadius='2xl' p={3} textAlign='center' cursor='default'
        bg={earned ? badge.color : 'gray.100'}
        opacity={earned ? 1 : 0.45}
        boxShadow={earned ? 'md' : 'none'}
        transition='all 0.2s'
        minW='72px'
      >
        <Text fontSize='2xl'>{badge.icon}</Text>
        <Text fontSize='10px' fontWeight='bold' color={earned ? 'white' : 'gray.400'} mt={1} lineHeight={1.2}>
          {badge.name}
        </Text>
      </Box>
    </Tooltip>
  )
}

function ExamHistoryCard({ row }) {
  const pct = row.quantity > 0 ? Math.round((row.score / row.quantity) * 100) : 0
  const scheme = getScoreScheme(pct)
  const dateStr = row.timestamp ? row.timestamp.slice(0, 11) : '-'
  return (
    <Flex
      bg='white' borderRadius='xl' p={3} align='center' gap={3}
      boxShadow='sm' border='1px solid' borderColor='gray.100'
    >
      <CircularProgress value={pct} color={`${scheme}.400`} trackColor='gray.100' size='56px' thickness='8px'>
        <CircularProgressLabel fontSize='11px' fontWeight='black' color={`${scheme}.600`}>
          {pct}%
        </CircularProgressLabel>
      </CircularProgress>
      <Box flex={1} minW={0}>
        <Text fontSize='sm' fontWeight='bold' color='gray.700' noOfLines={1}>{row.exam_name}</Text>
        <Text fontSize='xs' color='gray.500' noOfLines={1}>{row.course_name}</Text>
        <Text fontSize='xs' color='gray.400'>{dateStr}</Text>
      </Box>
      <Box textAlign='right' flexShrink={0}>
        <Text fontWeight='black' fontSize='lg' color={`${scheme}.500`}>{row.score}</Text>
        <Text fontSize='xs' color='gray.400'>/{row.quantity}</Text>
      </Box>
    </Flex>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function Profile() {
  const dispatch = useDispatch()
  const history = useHistory()
  const cardBg = useColorModeValue('white', 'gray.800')

  const initFetch = useCallback(() => {
    dispatch(GET_USER_BY_ID(Cookies.get('user_id')))
  }, [dispatch])
  useEffect(() => { initFetch() }, [initFetch])

  const data = useSelector((state) => state.users.user)
  const examHistory = data?.history || []

  const stats = useMemo(() => {
    if (!examHistory.length) return { totalExams: 0, bestPct: 0, avgPct: 0, totalScore: 0 }
    const pcts = examHistory.map(r => r.quantity > 0 ? (r.score / r.quantity) * 100 : 0)
    return {
      totalExams: examHistory.length,
      bestPct: Math.round(Math.max(...pcts)),
      avgPct: Math.round(pcts.reduce((a, b) => a + b, 0) / pcts.length),
      totalScore: examHistory.reduce((a, r) => a + (r.score || 0), 0),
    }
  }, [examHistory])

  const earnedBadges = useMemo(() => BADGE_DEFS.map(b => {
    if (b.type === 'count') return { ...b, earned: stats.totalExams >= b.threshold }
    if (b.type === 'avg') return { ...b, earned: stats.avgPct >= b.threshold }
    if (b.type === 'best') return { ...b, earned: stats.bestPct >= b.threshold }
    return { ...b, earned: false }
  }), [stats])

  const { nextMilestone, prevMilestone, milestoneProgress } = useMemo(() => {
    const next = COUNT_MILESTONES.find(m => m > stats.totalExams) ?? COUNT_MILESTONES[COUNT_MILESTONES.length - 1]
    const prev = [...COUNT_MILESTONES].reverse().find(m => m <= stats.totalExams) ?? 0
    const progress = next === prev ? 100 : Math.round(((stats.totalExams - prev) / (next - prev)) * 100)
    return { nextMilestone: next, prevMilestone: prev, milestoneProgress: progress }
  }, [stats.totalExams])

  const avatarIdx = useMemo(() => Math.floor((Math.random() * 10) + 1), [])
  const avatarSrc = require(`assets/img/avatars/g${avatarIdx}.png`)
  const motivation = getMotivation(stats.totalExams, stats.avgPct)
  const remaining = nextMilestone - stats.totalExams

  return (
    <Flex direction='column' pt={{ base: '80px', md: '75px' }} pb={8}>

      {/* ── Hero Banner ──────────────────────────────────────────────────── */}
      <Box
        bgGradient='linear(135deg, purple.500, teal.400)'
        borderRadius='2xl'
        p={{ base: 6, md: 8 }}
        mb={6}
        position='relative'
        overflow='hidden'
      >
        {/* decorative circles */}
        <Box position='absolute' top='-30px' right='-30px' w='150px' h='150px'
          borderRadius='full' bg='whiteAlpha.100' />
        <Box position='absolute' bottom='-20px' left='20%' w='100px' h='100px'
          borderRadius='full' bg='whiteAlpha.100' />

        <Flex direction={{ base: 'column', md: 'row' }} align='center' gap={5} position='relative'>
          <Avatar
            src={avatarSrc}
            w={{ base: '90px', md: '110px' }}
            h={{ base: '90px', md: '110px' }}
            border='4px solid white'
            boxShadow='0 0 0 4px rgba(255,255,255,0.3)'
            borderRadius='2xl'
          />
          <Box flex={1} textAlign={{ base: 'center', md: 'left' }}>
            <Flex align='center' gap={2} justify={{ base: 'center', md: 'flex-start' }} mb={1}>
              <Heading size='xl' color='white'>
                สวัสดี, {data?.firstname}! 👋
              </Heading>
            </Flex>
            <Text color='whiteAlpha.800' fontSize='md' mb={2}>
              {data?.class} · {data?.school}
            </Text>
            <Badge
              bg='whiteAlpha.300' color='white' px={3} py={1}
              borderRadius='full' fontSize='sm' fontWeight='bold'
            >
              {motivation}
            </Badge>
          </Box>
          <Button
            size='lg'
            bg='white'
            color='purple.600'
            fontWeight='black'
            borderRadius='xl'
            px={6}
            _hover={{ bg: 'yellow.100', transform: 'scale(1.05)' }}
            transition='all 0.2s'
            onClick={() => history.push('/user/mycourse')}
            flexShrink={0}
          >
            🚀 ไปทำข้อสอบ!
          </Button>
        </Flex>
      </Box>

      {/* ── Stat Cards ───────────────────────────────────────────────────── */}
      <SimpleGrid columns={{ base: 2, md: 4 }} gap={4} mb={6}>
        <StatMini icon='📝' label='ทำข้อสอบ' value={stats.totalExams} unit='ครั้ง' bg='blue.400' />
        <StatMini icon='🏆' label='คะแนนสูงสุด' value={`${stats.bestPct}%`} unit='ของครั้งที่ดีที่สุด' bg='orange.400' />
        <StatMini icon='📊' label='คะแนนเฉลี่ย' value={`${stats.avgPct}%`} unit='โดยเฉลี่ย' bg='teal.400' />
        <StatMini icon='⭐' label='คะแนนสะสม' value={stats.totalScore} unit='แต้มสะสม' bg='purple.500' />
      </SimpleGrid>

      <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={6} mb={6}>

        {/* ── Badges ─────────────────────────────────────────────────────── */}
        <Box bg={cardBg} borderRadius='2xl' p={5} boxShadow='sm'>
          <Flex align='center' gap={2} mb={4}>
            <Text fontSize='xl'>🏅</Text>
            <Heading size='md' color='gray.700'>เหรียญรางวัลของฉัน</Heading>
            <Badge colorScheme='teal' ml='auto'>
              {earnedBadges.filter(b => b.earned).length}/{earnedBadges.length}
            </Badge>
          </Flex>
          <Flex wrap='wrap' gap={2}>
            {earnedBadges.map((badge, i) => (
              <BadgeCard key={i} badge={badge} earned={badge.earned} />
            ))}
          </Flex>
        </Box>

        {/* ── Milestone + Profile ─────────────────────────────────────────── */}
        <Flex direction='column' gap={4}>

          {/* Milestone */}
          <Box bg={cardBg} borderRadius='2xl' p={5} boxShadow='sm'>
            <Flex align='center' gap={2} mb={3}>
              <Text fontSize='xl'>🎯</Text>
              <Heading size='md' color='gray.700'>เป้าหมายถัดไป</Heading>
            </Flex>
            <Flex justify='space-between' mb={1}>
              <Text fontSize='sm' color='gray.500'>ทำข้อสอบ {stats.totalExams} / {nextMilestone} ครั้ง</Text>
              <Text fontSize='sm' fontWeight='bold' color='teal.500'>{milestoneProgress}%</Text>
            </Flex>
            <Progress
              value={milestoneProgress}
              colorScheme='teal'
              size='lg'
              borderRadius='full'
              hasStripe
              isAnimated
              mb={2}
            />
            {remaining > 0 ? (
              <Text fontSize='sm' color='gray.500' textAlign='center'>
                🔥 อีก <strong>{remaining} ครั้ง</strong> จะได้ปลดล็อกเป้าหมายถัดไป!
              </Text>
            ) : (
              <Text fontSize='sm' color='teal.500' textAlign='center' fontWeight='bold'>
                🎉 ถึงเป้าหมายแล้ว! สุดยอดมาก!
              </Text>
            )}
          </Box>

          {/* Profile info */}
          <Box bg={cardBg} borderRadius='2xl' p={5} boxShadow='sm'>
            <Flex align='center' gap={2} mb={3}>
              <Text fontSize='xl'>📋</Text>
              <Heading size='md' color='gray.700'>ข้อมูลนักเรียน</Heading>
            </Flex>
            <Flex direction='column' gap={2}>
              {[
                { label: '👤 ชื่อ', value: `${data?.firstname || ''} ${data?.lastname || ''}` },
                { label: '🏫 โรงเรียน', value: data?.school },
                { label: '📚 ชั้นเรียน', value: data?.class },
                { label: '📱 เบอร์โทร', value: data?.mobile },
              ].map(({ label, value }) => (
                <Flex key={label} align='center' gap={2}>
                  <Text fontSize='sm' color='gray.500' minW='90px'>{label}</Text>
                  <Text fontSize='sm' fontWeight='semibold' color='gray.700' noOfLines={1}>
                    {value || '-'}
                  </Text>
                </Flex>
              ))}
            </Flex>
          </Box>
        </Flex>
      </Grid>

      {/* ── Exam History ─────────────────────────────────────────────────── */}
      <Box bg={cardBg} borderRadius='2xl' p={5} boxShadow='sm'>
        <Flex align='center' gap={2} mb={4}>
          <Text fontSize='xl'>📜</Text>
          <Heading size='md' color='gray.700'>ประวัติการทำข้อสอบ</Heading>
          {examHistory.length > 0 && (
            <Badge colorScheme='blue' ml='auto'>{examHistory.length} ครั้ง</Badge>
          )}
        </Flex>

        {examHistory.length === 0 ? (
          <Flex direction='column' align='center' py={10} gap={3}>
            <Text fontSize='5xl'>📝</Text>
            <Text fontWeight='bold' color='gray.600' fontSize='lg'>ยังไม่เคยทำข้อสอบเลย</Text>
            <Text color='gray.500' textAlign='center'>
              มาเริ่มทำข้อสอบแรกกัน แล้วคะแนนจะขึ้นมาที่นี่!
            </Text>
            <Button
              mt={2}
              colorScheme='teal'
              size='lg'
              borderRadius='xl'
              fontWeight='black'
              onClick={() => history.push('/user/mycourse')}
            >
              🚀 เริ่มทำข้อสอบเลย!
            </Button>
          </Flex>
        ) : (
          <>
            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' }} gap={3} maxH='420px' overflowY='auto'>
              {examHistory.map((row, i) => (
                <ExamHistoryCard key={i} row={row} />
              ))}
            </Grid>
            <Box mt={5} textAlign='center'>
              <Button
                colorScheme='teal'
                size='lg'
                borderRadius='xl'
                fontWeight='black'
                px={10}
                _hover={{ transform: 'scale(1.05)' }}
                transition='all 0.2s'
                onClick={() => history.push('/user/mycourse')}
              >
                🚀 ไปทำข้อสอบเพิ่มอีก!
              </Button>
            </Box>
          </>
        )}
      </Box>

    </Flex>
  )
}
